import React from 'react';
import { Text } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { Lock } from 'lucide-react-native';
import {
  getStripeInfoBackgroundColor,
  getStripeInfoTextColor,
  getStripeInfoIconColor,
  BORDER_RADIUS_MEDIUM,
  FONT_SIZE_SM,
  ICON_SIZE_SMALL,
} from '../constants';
import AmountSelector from './AmountSelector';
import PaymentMethodSelector from './PaymentMethodSelector';
import CardPaymentForm from './CardPaymentForm';
import BlikPaymentForm from './BlikPaymentForm';
import PaymentSummary from './PaymentSummary';

export type PrepaidTabProps = {
  isDark: boolean;
  textColor: string;
  subtextColor: string;
  selectedAmount: number | null;
  customAmount: string;
  selectedPaymentMethod: string | null;
  cardNumber: string;
  cardExpiry: string;
  cardCVC: string;
  cardName: string;
  blikCode: string;
  finalAmount: number | null;
  canProceed: boolean;
  onAmountSelect: (amount: number) => void;
  onCustomAmountFocus: () => void;
  onCustomAmountChange: (text: string) => void;
  onSelectPaymentMethod: (methodId: string) => void;
  onCardNumberChange: (text: string) => void;
  onCardExpiryChange: (text: string) => void;
  onCardCVCChange: (text: string) => void;
  onCardNameChange: (text: string) => void;
  onBlikCodeChange: (text: string) => void;
  onPayment: () => void;
};

const PrepaidTab: React.FC<PrepaidTabProps> = React.memo(({
  isDark,
  textColor,
  subtextColor,
  selectedAmount,
  customAmount,
  selectedPaymentMethod,
  cardNumber,
  cardExpiry,
  cardCVC,
  cardName,
  blikCode,
  finalAmount,
  canProceed,
  onAmountSelect,
  onCustomAmountFocus,
  onCustomAmountChange,
  onSelectPaymentMethod,
  onCardNumberChange,
  onCardExpiryChange,
  onCardCVCChange,
  onCardNameChange,
  onBlikCodeChange,
  onPayment,
}) => {
  return (
    <YStack space="$4">
      {/* Stripe Security Info */}
      <XStack
        backgroundColor={getStripeInfoBackgroundColor(isDark)}
        padding="$3"
        borderRadius={BORDER_RADIUS_MEDIUM}
        alignItems="center"
        gap="$2"
      >
        <Lock size={ICON_SIZE_SMALL} color={getStripeInfoIconColor(isDark)} />
        <Text fontSize={FONT_SIZE_SM} fontWeight="500" color={getStripeInfoTextColor(isDark, textColor)}>
          Płatności zabezpieczone przez Stripe
        </Text>
      </XStack>

      {/* Amount Selector */}
      <AmountSelector
        isDark={isDark}
        textColor={textColor}
        subtextColor={subtextColor}
        selectedAmount={selectedAmount}
        customAmount={customAmount}
        onAmountSelect={onAmountSelect}
        onCustomAmountFocus={onCustomAmountFocus}
        onCustomAmountChange={onCustomAmountChange}
      />

      {/* Payment Method Selector */}
      <PaymentMethodSelector
        isDark={isDark}
        textColor={textColor}
        subtextColor={subtextColor}
        selectedPaymentMethod={selectedPaymentMethod}
        onSelectPaymentMethod={onSelectPaymentMethod}
      />

      {/* Card Payment Form */}
      {selectedPaymentMethod === 'card' && (
        <CardPaymentForm
          isDark={isDark}
          textColor={textColor}
          subtextColor={subtextColor}
          cardNumber={cardNumber}
          cardExpiry={cardExpiry}
          cardCVC={cardCVC}
          cardName={cardName}
          onCardNumberChange={onCardNumberChange}
          onCardExpiryChange={onCardExpiryChange}
          onCardCVCChange={onCardCVCChange}
          onCardNameChange={onCardNameChange}
        />
      )}

      {/* BLIK Code Form */}
      {selectedPaymentMethod === 'blik' && (
        <BlikPaymentForm
          isDark={isDark}
          textColor={textColor}
          subtextColor={subtextColor}
          blikCode={blikCode}
          onBlikCodeChange={onBlikCodeChange}
        />
      )}

      {/* Payment Summary */}
      {finalAmount && finalAmount > 0 && (
        <PaymentSummary
          isDark={isDark}
          textColor={textColor}
          subtextColor={subtextColor}
          finalAmount={finalAmount}
          canProceed={canProceed}
          onPayment={onPayment}
        />
      )}
    </YStack>
  );
});

PrepaidTab.displayName = 'PrepaidTab';

export default PrepaidTab;

